import { inject, Injectable } from '@angular/core';
import { SqliteService } from '@services/sqlite/sqlite.service';
import { Environment } from '@environment/environment';
import { PerformUrl } from '@constants/api-url.constant';
import { HttpService } from '@services/http/http.service';
import { AjaxResponse } from '@models/types/ajax-response.type';
import { BackBoneResponseDTO, BackBoneVisit, BackBoneVisits } from '@models/dto/backbone.dto';
import { PerformIdbService } from '@services/perform/perform-idb/perform-idb.service';
import { BackboneTable, VisitDbSyncTaskStatus } from '../perform/perform-idb/backbone-idb.types';
import { MsalAuthService } from '@src/msal/msal-auth.service';
import { GenericError } from '@models/classes/generic.error';
import { ErrorCode } from '@models/enums/error.codes';

@Injectable({
  providedIn: 'root'
})
export class VisitListService {

  private readonly sqliteSvc = inject(SqliteService);
  private readonly performIDBSvc = inject(PerformIdbService);
  readonly #msalAuthSvc = inject(MsalAuthService);
  readonly #httpSvc: HttpService = inject(HttpService);
  constructor() { }

  isDBExists(visitNO: string) {
    return this.sqliteSvc.dbExists(visitNO + '.db');
  }

  getActiveUserName() {
    const userName = this.#msalAuthSvc.activeUserName;
    if(!userName) {
      throw new GenericError({
        errorCode : ErrorCode.NULL_OR_EMPTY,
        message : 'Username not found',
        location : 'visit-list.service>getActiveUserName()'
      })
    }
    return userName;
  }

  #mapVisitData(visitData: BackBoneResponseDTO[]): BackBoneVisits {
    const visitPageData: BackBoneVisit[] = visitData.map((visit: BackBoneResponseDTO) => {
      const data: BackBoneVisit = {
        ...visit,
        audit_no: visit.audit_no.split(','),
        audit_types: visit.audit_types.split(','),
        seq : visit.visit_no + this.#msalAuthSvc.activeUserName as string,
        lastSyncDate : (visit as any).lastSyncDate ?? null,
        syncStatus : (visit as any).syncStatus ?? VisitDbSyncTaskStatus.NotDownloaded
      };

      return data;
    })

    const created = visitPageData.filter((visit) => visit.audit_status === 'CREATED');
    const pending = visitPageData.filter((visit) => visit.audit_status !== 'CREATED');
    return { created, completed: pending }
  }


  async fetchNewBackboneVisits() {
    const url = Environment.baseUrl + PerformUrl.downloadVisitData;
    const response = await this.#httpSvc.get<AjaxResponse<BackBoneResponseDTO[]>>([url], true);
    const backboneTableData: BackboneTable[] = response.data.map(item => {
      const userName = this.#msalAuthSvc.activeUserName as string;
      const seq = item.visit_no + userName;
      let _item: BackboneTable = { seq, userName, lastSyncDate: null,syncStatus : VisitDbSyncTaskStatus.NotDownloaded , ...item }
      return _item
    })
    await this.performIDBSvc.addBackboneVisits(backboneTableData);
    const mappedData = this.#mapVisitData(response.data);
    return mappedData
  }

  async fetchVisits(localOnlyIfAvailable : boolean = false): Promise<BackBoneVisits> {
    const backboneData: BackboneTable[] = await this.performIDBSvc.getBackboneVisits(this.#msalAuthSvc.activeUserName as string);

    if (backboneData?.length == 0) {
      return await this.fetchNewBackboneVisits();
    } else {
      if (localOnlyIfAvailable) {
        return this.#mapVisitData(backboneData);
      }
      this.fetchNewBackboneVisits();
      const mappedData = this.#mapVisitData(backboneData);
      return mappedData;
    }

  }

  async isFutureDateVisit(visitNo : string) : Promise<boolean> {
    
    const scheduledStartDateTime : Date  = await this.performIDBSvc.getScheduleDate(visitNo, this.#msalAuthSvc.activeUserName as string) 
    if(scheduledStartDateTime) {
      const today = new Date();
      today.setHours(23,59,59,0);
      if(today.getTime() <= scheduledStartDateTime.getTime()) {
        return true;
      } else {
          return false;
      }
    } else {
      return false;
    }
  }



}
